import React, { useEffect, useMemo, useState } from 'react';
import { Droppable, Draggable, Direction } from 'react-beautiful-dnd';
import { HiOutlineExclamation } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import {
  Flex,
  Icon,
  Stack,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  updatesGlobalDate,
  useAppDispatch,
  useAppSelector,
  useGetAgeChartDataQuery,
  useGetEntranceExitChartDataQuery,
  useGetKmChartDataQuery,
  useGetModelsCarsQuery,
  useGetRepairChartDataQuery,
  useGetTotalChartDataQuery,
  useSetSectionsMutation,
} from '@spartalabs/pdc-core';
import {
  BarChart,
  Card,
  ChartCard,
  Container,
  LineChart,
  MapCard,
  MultiInfoCard,
} from 'components';
import ResizableCard from 'components/Cards/ResizableCard';
import format from 'date-fns/format';
import { useChart } from 'hooks/useChart';
import { useDebounce } from 'hooks/useDebounce';
import { colors } from 'theme/components/colors';
import { ChartTypeFilters, emptyData } from 'utils/constants';
import {
  allTypesData,
  checkDateRange,
  getBarsColor,
  getFilteredColor,
  getMapColors,
  getRegionsOrStatesByRegion,
  regionDtoToRegion,
  regionToRegionDto,
  typeFilterToTypeFilterDto,
  validatesValue,
} from 'utils/functions';
import WelcomeModal from './components/WelcomeModal';

export const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { step, dashboardConfig } = useAppSelector(
    state => state.authSlicer.user,
  );

  const { globalDate, globalDateUpdate, globalPeriod, dashboardCards } =
    useAppSelector(state => state.globalDataSlicer);

  const droppableDir: Direction | undefined = useBreakpointValue({
    base: 'vertical',
    lg: 'horizontal',
  });

  //#region <DASHBOARD CARDS>
  const [setSections] = useSetSectionsMutation();

  const { debouncedValue } = useDebounce(JSON.stringify(dashboardCards), 1000);

  const rows: { content: Widget[] }[] = useMemo(() => {
    return dashboardCards;
  }, [dashboardCards]);

  useEffect(() => {
    const setSectionsFunc = async () => {
      await setSections(JSON.parse(debouncedValue));
    };

    if (debouncedValue !== JSON.stringify(dashboardConfig)) setSectionsFunc();
  }, [debouncedValue]);
  //#endregion

  const navigate = useNavigate();

  const { handleChartData, handleChartLabels } = useChart();

  //#step <Onboardin>
  const isOnboarding = useMemo(() => step === 1 || step === 0, [step]);

  //#endstep

  //#region <BRAZIL QUANTITIES>
  const {
    data: totalCharts,
    isLoading: isLoadingTotalCharts,
    isFetching: isFetchingTotalCharts,
    isError: isErrorTotalCharts,
    refetch: refetchTotalCharts,
  } = useGetTotalChartDataQuery({ region: undefined });
  //#endregion

  //#region <SERVICES CHARTS>
  const [pickedServicesPeriod, setPickedServicesPeriod] =
    useState(globalPeriod);
  const [servicesDateRange, setServicesDateRange] = useState(
    checkDateRange(globalDate),
  );
  const [servicesTypeFilter, setServicesTypeFilter] = useState<ChartTypeFilter>(
    ChartTypeFilters[0],
  );
  const [servicesChartLabels, setServicesChartLabels] = useState(['']);
  const [servicesChartData, setServicesChartData] = useState<
    ChartData[] | null
  >(null);

  const {
    data: servicesChartValues,
    isLoading: isLoadingServicesChart,
    isFetching: isFetchingServicesChart,
    isError: isErrorServicesChart,
    refetch: refetchServicesChart,
  } = useGetRepairChartDataQuery({
    dimension: pickedServicesPeriod,
    type: typeFilterToTypeFilterDto(servicesTypeFilter.type),
    startDate: format(
      checkDateRange(servicesDateRange)[0].startDate || new Date(),
      'yyyy-MM-dd',
    ),

    endDate: format(
      checkDateRange(servicesDateRange)[0].endDate || new Date(),
      'yyyy-MM-dd',
    ),
  });

  useEffect(() => {
    if (servicesChartValues) {
      const labels = handleChartLabels(
        pickedServicesPeriod,
        servicesChartValues,
        servicesDateRange[0].startDate,
        servicesDateRange[0].endDate,
      );

      setServicesChartLabels(labels);

      const chart = handleChartData(
        labels,
        servicesTypeFilter.type === 'total'
          ? allTypesData(servicesChartValues.itens, labels)
          : [
              {
                label: 'Quantidade',
                values: servicesChartValues.itens,
                fill: {
                  type: 'start',
                  color: getFilteredColor(servicesTypeFilter?.type),
                },
                accessor: 'quantity',
              },
            ],
      );

      setServicesChartData(chart);
    }
  }, [servicesChartValues]);
  //#endregion

  //#region <SERVICES MULTINFO>
  const [servicesMIDateRange, setServicesMIDateRange] = useState(
    checkDateRange(globalDate),
  );

  const {
    data: servicesMultiInfoData,
    isLoading: isLoadingServicesMultiInfo,
    isFetching: isFetchingServicesMultiInfo,
    isError: isErrorServicesMultiInfo,
    refetch: refetchServicesMultiInfo,
  } = useGetRepairChartDataQuery({
    dimension: 'Mês',
    startDate: format(
      checkDateRange(servicesMIDateRange)[0].startDate || new Date(),
      'yyyy-MM-dd',
    ),
    endDate: format(
      checkDateRange(servicesMIDateRange)[0].endDate || new Date(),
      'yyyy-MM-dd',
    ),
  });
  //#endregion

  //#region <ENTRANCE AND EXIT CHART>
  const [entranceExitPickedPeriod, setEntranceExitPickedPeriod] =
    useState(globalPeriod);
  const [entranceExitDateRange, setEntranceExitDateRange] = useState(
    checkDateRange(globalDate),
  );
  const [entranceExitChartLabels, setEntranceExitChartLabels] = useState(['']);
  const [entranceExitChartData, setEntranceExitChartData] = useState<
    ChartData[] | null
  >(null);

  const {
    data: entranceExitChartValues,
    isLoading: isLoadingEntranceExitChart,
    isFetching: isFetchingEntranceExitChart,
    isError: isErrorEntranceExitChart,
    refetch: refetchEntranceExitChart,
  } = useGetEntranceExitChartDataQuery({
    dimension: entranceExitPickedPeriod,
    endDate: format(
      checkDateRange(entranceExitDateRange)[0].endDate || new Date(),
      'yyyy-MM-dd',
    ),
    startDate: format(
      checkDateRange(entranceExitDateRange)[0].startDate || new Date(),
      'yyyy-MM-dd',
    ),
  });

  useEffect(() => {
    if (entranceExitChartValues) {
      const labels = handleChartLabels(
        entranceExitPickedPeriod,
        entranceExitChartValues,
        entranceExitDateRange[0].startDate,
        entranceExitDateRange[0].endDate,
      );

      setEntranceExitChartLabels(labels);

      const chart = handleChartData(labels, [
        {
          label: 'Entrada',
          borderColor: colors.blue['100'],
          values: entranceExitChartValues,
          accessor: 'entrance',
        },
        {
          label: 'Saída',
          borderColor: colors.blue['200'],
          values: entranceExitChartValues,
          accessor: 'exit',
        },
      ]);

      setEntranceExitChartData(chart);
    }
  }, [entranceExitChartValues]);
  //#endregion

  //#region <AGE/KM MAP>
  const [selected, setSelected] = useState('Brasil');
  const [mapPicked, setMapPicked] = useState('Idade');

  const {
    data: ageCharts,
    isLoading: isLoadingAgeCharts,
    isFetching: isFetchingAgeCharts,
    isError: isErrorAgeCharts,
    refetch: refetchAgeCharts,
  } = useGetAgeChartDataQuery({
    region: selected === 'Brasil' ? undefined : selected?.toUpperCase(),
  });

  const { data: ageChartsSelect } = useGetAgeChartDataQuery({
    region: undefined,
  });

  const {
    data: kmCharts,
    isLoading: isLoadingKmCharts,
    isFetching: isFetchingKmCharts,
    isError: isErrorKmCharts,
    refetch: refetchKmCharts,
  } = useGetKmChartDataQuery({
    region: selected === 'Brasil' ? undefined : selected?.toUpperCase(),
  });

  const { data: kmChartsSelect } = useGetKmChartDataQuery({
    region: undefined,
  });

  const ageKmSelect = useMemo(() => {
    if (mapPicked === 'Idade') return ageChartsSelect;

    if (mapPicked === 'Km') return kmChartsSelect;

    return undefined;
  }, [ageCharts, kmCharts, mapPicked, selected]);

  const isErrorAgeKm = useMemo(() => {
    if (mapPicked === 'Idade') return isErrorAgeCharts;

    if (mapPicked === 'Km') return isErrorKmCharts;

    return false;
  }, [mapPicked, isErrorAgeCharts, isErrorKmCharts]);

  const refetchAgeKm = useMemo(() => {
    if (mapPicked === 'Idade') return refetchAgeCharts;

    if (mapPicked === 'Km') return refetchKmCharts;

    return undefined;
  }, [mapPicked]);

  const ageKmData = useMemo(() => {
    if (mapPicked === 'Idade') return ageCharts;

    if (mapPicked === 'Km') return kmCharts;

    return undefined;
  }, [ageCharts, kmCharts, mapPicked, selected]);

  const mapSelectLabels = useMemo(() => {
    const selectArr: string[] = [];

    Object.keys(ageKmSelect ? ageKmSelect.count : {}).map(key => {
      if (validatesValue(key, ageKmSelect.count[key])) {
        return selectArr.push(regionToRegionDto(key));
      }
    });

    return selectArr;
  }, [ageKmSelect]);
  //#endregion

  //#region <MODELS MULTINFO>
  const {
    data: modelsData,
    isFetching: isFetchingModels,
    isLoading: isLoadingModels,
    isError: isErrorModels,
    refetch: refetchModels,
  } = useGetModelsCarsQuery();
  //#endregion

  //#region <UPDATES GLOBAL DATES AND PERIODS>
  useEffect(() => {
    setEntranceExitPickedPeriod(globalPeriod);
    setPickedServicesPeriod(globalPeriod);
    if (globalDateUpdate) {
      setEntranceExitDateRange(globalDate);
      setServicesMIDateRange(globalDate);
      setServicesDateRange(globalDate);
      dispatch(updatesGlobalDate(false));
    }
  }, [globalDate, globalDateUpdate]);

  useEffect(() => {
    setEntranceExitPickedPeriod(globalPeriod);
    setPickedServicesPeriod(globalPeriod);
    setEntranceExitDateRange(globalDate);
    setServicesMIDateRange(globalDate);
    setServicesDateRange(globalDate);
  }, [globalPeriod]);
  //#endregion

  return (
    <Container pt="0px" mt="0px">
      <WelcomeModal isOpen={isOnboarding} />

      <Droppable
        droppableId="DroppableRows"
        type="DroppableRows"
        renderClone={provided => (
          <Card
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            cardRef={provided.innerRef}
            opacity="65%"
          />
        )}
      >
        {provided => {
          if (rows.length === 0)
            return (
              <VStack
                // border="1px dashed"
                // borderColor="grey.600"
                spacing="2px"
                {...provided.droppableProps}
                ref={provided.innerRef}
                justifyContent="center"
                minH="50vh"
              >
                <Icon
                  as={HiOutlineExclamation}
                  h="72px"
                  w="72px"
                  color="text.300"
                  mb="8px"
                />
                <Text
                  textAlign="center"
                  fontSize="24px"
                  fontWeight="500"
                  color="text.100"
                >
                  Arraste e solte os quadros aqui!
                </Text>
                <Text textAlign="center" fontSize="14px" color="text.300">
                  Abra o painel Personalizar para começar
                </Text>
              </VStack>
            );

          return (
            <VStack
              pb="20px"
              w="100%"
              minH="70vh"
              fontSize="xl"
              spacing="20px"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {rows?.map(({ content }, rowIndex) => {
                return (
                  <Draggable
                    key={`DraggableRow${rowIndex}`}
                    draggableId={`DraggableRow${rowIndex}`}
                    index={rowIndex}
                  >
                    {provided => {
                      return (
                        <>
                          <Flex
                            border="1px solid transparent"
                            _hover={{ borderColor: 'grey.500' }}
                            transition="all 0.2s"
                            fontSize="xl"
                            w="100%"
                            align="flex-start"
                            position="relative"
                            ref={provided.innerRef}
                            borderRadius="4px"
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Droppable
                              droppableId={`DroppableRow${rowIndex}`}
                              direction={droppableDir}
                              type="DroppableRow"
                              renderClone={provided => (
                                <Card
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  cardRef={provided.innerRef}
                                  opacity="50%"
                                />
                              )}
                            >
                              {provided => {
                                return (
                                  <Stack
                                    {...provided.droppableProps}
                                    p="2px"
                                    direction={{
                                      base: 'column',
                                      lg: 'row',
                                    }}
                                    ref={provided.innerRef}
                                    alignContent="flex-start"
                                    justify="space-between"
                                    w="100%"
                                    alignItems="stretch"
                                  >
                                    {content.map((widget, cardIndex) => {
                                      const { id, size, type, canBeHalf } =
                                        widget;

                                      return (
                                        <Draggable
                                          key={`DraggableItem${id}`}
                                          draggableId={`DraggableItem${id}`}
                                          index={cardIndex}
                                          isDragDisabled={size === '100%'}
                                        >
                                          {provided => {
                                            if (id === 0) {
                                              return (
                                                <MapCard
                                                  cardRef={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  cardOptions={{
                                                    cardIndex,
                                                    widget,
                                                    rowIndex,
                                                    dragHandler: provided,
                                                  }}
                                                  cardName="regions"
                                                  fetching={
                                                    isFetchingTotalCharts
                                                  }
                                                  loading={isLoadingTotalCharts}
                                                  details={() =>
                                                    navigate(
                                                      '/relatorios/distdefrotas',
                                                    )
                                                  }
                                                  subtitleMb="20px"
                                                  w={{
                                                    base: '100%',
                                                    lg: size,
                                                  }}
                                                  mapW="300px"
                                                  mapType="Brazil"
                                                  mapColor={getMapColors(
                                                    totalCharts &&
                                                      totalCharts.count,
                                                  )}
                                                  hasMap
                                                  customTitle={
                                                    <Text
                                                      alignSelf="center"
                                                      color="text.100"
                                                      fontSize="16px"
                                                      fontWeight={400}
                                                    >
                                                      Regiões do <b>Brasil</b>
                                                    </Text>
                                                  }
                                                  subtitle="Distribuição de frota total pelo Brasil"
                                                  data={getRegionsOrStatesByRegion(
                                                    totalCharts &&
                                                      totalCharts.count,
                                                    'Brazil',
                                                  )}
                                                  isError={isErrorTotalCharts}
                                                  refetch={refetchTotalCharts}
                                                />
                                              );
                                            }

                                            if (id === 1) {
                                              return (
                                                <ChartCard
                                                  from="home"
                                                  cardRef={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  cardOptions={{
                                                    cardIndex,
                                                    widget,
                                                    rowIndex,
                                                    dragHandler: provided,
                                                  }}
                                                  cardName="services"
                                                  details={() =>
                                                    navigate(
                                                      '/relatorios/manutencao/servicos',
                                                    )
                                                  }
                                                  pickerType="date"
                                                  picker={['Dia', 'Mês']}
                                                  firstPicked={
                                                    pickedServicesPeriod
                                                  }
                                                  setPicked={
                                                    setPickedServicesPeriod
                                                  }
                                                  startDate={servicesDateRange}
                                                  setDateRange={
                                                    setServicesDateRange
                                                  }
                                                  setFilter={
                                                    setServicesTypeFilter
                                                  }
                                                  filters={ChartTypeFilters}
                                                  selectedFilter={
                                                    servicesTypeFilter
                                                  }
                                                  w={{
                                                    base: '100%',
                                                    lg: size,
                                                  }}
                                                  subtitleMb="20px"
                                                  customTitle={
                                                    <Text
                                                      alignSelf="center"
                                                      color="text.100"
                                                      fontSize="16px"
                                                      fontWeight={400}
                                                    >
                                                      Gráfico de
                                                      <b> Serviços</b>
                                                    </Text>
                                                  }
                                                  subtitle="Comparativo com os principais serviços realizados. Se preferir filtre pelos serviços abaixo"
                                                  isError={isErrorServicesChart}
                                                  refetch={refetchServicesChart}
                                                  isLoading={
                                                    isLoadingServicesChart
                                                  }
                                                  skeletonType="bar"
                                                >
                                                  <Flex
                                                    w="100%"
                                                    overflow="auto"
                                                    justify="center"
                                                  >
                                                    <BarChart
                                                      stackedY={true}
                                                      stackedX={true}
                                                      loading={
                                                        isFetchingServicesChart
                                                      }
                                                      w="800px"
                                                      labels={
                                                        servicesChartLabels
                                                      }
                                                      data={
                                                        servicesChartData ||
                                                        emptyData
                                                      }
                                                    />
                                                  </Flex>
                                                </ChartCard>
                                              );
                                            }

                                            if (id === 2) {
                                              return (
                                                <MultiInfoCard
                                                  cardRef={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  cardOptions={{
                                                    cardIndex,
                                                    widget,
                                                    rowIndex,
                                                    dragHandler: provided,
                                                  }}
                                                  cardName="total-services"
                                                  fetching={
                                                    isFetchingServicesMultiInfo
                                                  }
                                                  loading={
                                                    isLoadingServicesMultiInfo
                                                  }
                                                  showTotal={true}
                                                  setDateRange={
                                                    setServicesMIDateRange
                                                  }
                                                  startDate={
                                                    servicesMIDateRange
                                                  }
                                                  details={() =>
                                                    navigate(
                                                      '/relatorios/manutencao/servicos',
                                                    )
                                                  }
                                                  w={{
                                                    base: '100%',
                                                    lg: size,
                                                  }}
                                                  title="Atendimentos Realizados"
                                                  subtitle="Quantidade por tipo de serviço"
                                                  data={servicesMultiInfoData}
                                                  isError={
                                                    isErrorServicesMultiInfo
                                                  }
                                                  refetch={
                                                    refetchServicesMultiInfo
                                                  }
                                                />
                                              );
                                            }

                                            if (id === 3)
                                              return (
                                                <ChartCard
                                                  cardRef={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  cardOptions={{
                                                    cardIndex,
                                                    widget,
                                                    rowIndex,
                                                    dragHandler: provided,
                                                  }}
                                                  cardName="exit-entrace"
                                                  details={() =>
                                                    navigate(
                                                      '/relatorios/manutencao/entradasaida',
                                                    )
                                                  }
                                                  pickerType="date"
                                                  picker={['Dia', 'Mês']}
                                                  firstPicked={
                                                    entranceExitPickedPeriod
                                                  }
                                                  setPicked={
                                                    setEntranceExitPickedPeriod
                                                  }
                                                  startDate={
                                                    entranceExitDateRange
                                                  }
                                                  setDateRange={
                                                    setEntranceExitDateRange
                                                  }
                                                  from="home"
                                                  customTitle={
                                                    <Text
                                                      alignSelf="center"
                                                      color="text.100"
                                                      fontSize="16px"
                                                      fontWeight={400}
                                                    >
                                                      Entrada e Saída de
                                                      <b> Manutenções</b>
                                                    </Text>
                                                  }
                                                  subtitle="Comparativo sobre a relação entre entrada e saída de veículos em manutenções"
                                                  w={{
                                                    base: '100%',
                                                    lg: size,
                                                  }}
                                                  isError={
                                                    isErrorEntranceExitChart
                                                  }
                                                  refetch={
                                                    refetchEntranceExitChart
                                                  }
                                                  isLoading={
                                                    isLoadingEntranceExitChart
                                                  }
                                                  skeletonType="line"
                                                >
                                                  <Flex
                                                    w="100%"
                                                    overflow="auto"
                                                    justify="center"
                                                  >
                                                    <LineChart
                                                      loading={
                                                        isFetchingEntranceExitChart
                                                      }
                                                      w="800px"
                                                      labels={
                                                        entranceExitChartLabels
                                                      }
                                                      data={
                                                        entranceExitChartData ||
                                                        emptyData
                                                      }
                                                    />
                                                  </Flex>
                                                </ChartCard>
                                              );

                                            if (id === 4)
                                              return (
                                                <MapCard
                                                  cardRef={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  cardOptions={{
                                                    cardIndex,
                                                    widget,
                                                    rowIndex,
                                                    dragHandler: provided,
                                                  }}
                                                  cardName="map"
                                                  details={() =>
                                                    navigate(
                                                      '/relatorios/distdefrotas',
                                                    )
                                                  }
                                                  fetching={
                                                    isFetchingAgeCharts ||
                                                    isFetchingKmCharts
                                                  }
                                                  loading={
                                                    isLoadingKmCharts ||
                                                    isLoadingAgeCharts
                                                  }
                                                  setSelected={setSelected}
                                                  selected={selected}
                                                  mapColor={
                                                    selected === 'Brasil'
                                                      ? getMapColors(
                                                          ageKmSelect?.count,
                                                        )
                                                      : {
                                                          ...getMapColors(
                                                            ageKmSelect?.count,
                                                            'Brasil',
                                                          ),
                                                          [regionDtoToRegion(
                                                            selected,
                                                          )]: true,
                                                        }
                                                  }
                                                  isMapClickable
                                                  // mb="20px"
                                                  w={{
                                                    base: '100%',
                                                    lg: size,
                                                  }}
                                                  select={mapSelectLabels}
                                                  hasMap
                                                  mapW="250px"
                                                  mapH="250px"
                                                  hasBars
                                                  setPicked={setMapPicked}
                                                  firstPicked={mapPicked}
                                                  barsColor={getBarsColor(
                                                    selected,
                                                    type,
                                                  )}
                                                  picker={['Idade', 'Km']}
                                                  customTitle={
                                                    <Text
                                                      alignSelf="center"
                                                      color="text.100"
                                                      fontSize="16px"
                                                      fontWeight={400}
                                                    >
                                                      Regiões por
                                                      <b>
                                                        {' '}
                                                        Idade e Km - Regiões
                                                      </b>
                                                    </Text>
                                                  }
                                                  title=" "
                                                  subtitle="Separada em cada estado como estão distribuídos por idade e km a sua frota"
                                                  data={
                                                    ageKmData?.count[
                                                      selected === 'Brasil'
                                                        ? 'br'
                                                        : regionDtoToRegion(
                                                            selected,
                                                          )
                                                    ] || {}
                                                  }
                                                  isError={isErrorAgeKm}
                                                  refetch={refetchAgeKm}
                                                />
                                              );

                                            if (id === 5)
                                              return (
                                                <MultiInfoCard
                                                  cardRef={provided.innerRef}
                                                  {...provided.draggableProps}
                                                  cardOptions={{
                                                    cardIndex,
                                                    widget,
                                                    rowIndex,
                                                    dragHandler: provided,
                                                  }}
                                                  cardName="models"
                                                  w={{
                                                    base: '100%',
                                                    lg: size,
                                                  }}
                                                  customTitle={
                                                    <Text
                                                      alignSelf="center"
                                                      color="text.100"
                                                      fontSize="16px"
                                                      fontWeight={400}
                                                    >
                                                      Distribuição da Frota por
                                                      <b> Modelo</b>
                                                    </Text>
                                                  }
                                                  subtitle="Como estão distribuídos os modelos em sua frota"
                                                  details={() =>
                                                    navigate(
                                                      '/relatorios/modelos',
                                                    )
                                                  }
                                                  fetching={isFetchingModels}
                                                  loading={isLoadingModels}
                                                  data={modelsData}
                                                  isError={isErrorModels}
                                                  refetch={refetchModels}
                                                />
                                              );

                                            return (
                                              <ResizableCard
                                                rowIndex={rowIndex}
                                                cardId={id}
                                                ref={provided.innerRef}
                                                canBeHalf={canBeHalf}
                                                w={{
                                                  base: '100%',
                                                  lg: size,
                                                }}
                                                {...provided.draggableProps}
                                                dragHandler={provided}
                                              >
                                                <Text
                                                  fontSize="24px"
                                                  color="text.100"
                                                >
                                                  {id}
                                                </Text>
                                                <Text color="text.100">
                                                  {type}
                                                </Text>
                                              </ResizableCard>
                                            );
                                          }}
                                        </Draggable>
                                      );
                                    })}

                                    {provided.placeholder}
                                  </Stack>
                                );
                              }}
                            </Droppable>
                          </Flex>
                        </>
                      );
                    }}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </VStack>
          );
        }}
      </Droppable>
    </Container>
  );
};
